import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import Seo from "@/components/Seo";
import AuthShell from "@/components/AuthShell";
import CTAButton from "@/components/CTAButton";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setStatus("error");
      setMessage("This verification link is missing or invalid.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setStatus("success");
        setMessage(data.message || "Email verified.");
        checkAuth();
      } catch (err) {
        setStatus("error");
        setMessage(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Seo title="Verify email" description="Verify your StUpEco Workspace email address." path="/verify-email" />
      <AuthShell
        title={status === "success" ? "Email verified" : status === "error" ? "Verification failed" : "Verifying…"}
        subtitle={status === "loading" ? "Hang tight while we confirm your email." : ""}
      >
        <div className="flex flex-col items-center gap-5 text-center" data-testid="verify-email-result">
          {status === "loading" && <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" />}
          {status === "success" && (
            <>
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-teal/12 text-brand-teal">
                <CheckCircle2 className="h-7 w-7" />
              </span>
              <p className="text-sm text-muted-foreground">{message}</p>
              <CTAButton to="/app" variant="gradient" size="md" data-testid="verify-go-app-btn">Go to Command Center</CTAButton>
            </>
          )}
          {status === "error" && (
            <>
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <XCircle className="h-7 w-7" />
              </span>
              <p className="text-sm text-muted-foreground">{message}</p>
              <div className="flex gap-3">
                <CTAButton to="/app" variant="outline" size="md">Open Workspace</CTAButton>
                <CTAButton to="/login" variant="primary" size="md">Log in</CTAButton>
              </div>
            </>
          )}
        </div>
      </AuthShell>
    </>
  );
}
